import React, {useEffect, useMemo, useState} from 'react';
import {AuthInput} from '../../../Interface/Forms/Inputs/AuthInput';
import {cpf as cpfValidator} from 'cpf-cnpj-validator';
import {
  Form,
  FormControl,
  Label,
  Subtitle,
  Title,
  OutlineButtonContainer,
  Footer,
  SignupRow,
  SignupText,
  SignupLink,
  SignupLinkText,
  FormHeader,
  FormInfos,
} from '../styles';
import {api} from '../../../../services/api';
import {Button} from '../../../Interface/Forms/Buttons/Button';
import {useAuth} from '../../../../context/auth';
import {getInstallAttributionData} from '../../../../utils/installAttribution';

interface Props {
  onBack: () => void;
  onSuccess?: () => void;
  onSignup?: () => void;
  sessionExpired?: boolean;
  onForgotPassword: () => void;
}

export default function SigninForm({
  onSuccess = () => {},
  onSignup,
  sessionExpired,
  onForgotPassword,
}: Props) {
  const {stringToHex, signIn, setShowSign, clientId} = useAuth();

  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [cpfIsValid, setCpfIsValid] = useState(false);

  const [sessionExpiredState, setSessionExpiredState] =
    useState(sessionExpired);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const isSigninEnabled = useMemo(
    () => cpfIsValid && password.length >= 4,
    [cpfIsValid, password],
  );

  function handleCpfChangeText(raw: string, extracted: string) {
    setCpfIsValid(false);
    if (/[a-zA-Z@]/.test(raw)) {
      setCpfError('Digite um CPF válido (apenas números)');
      return;
    }
    setCpfError('');
    setCpf(extracted);
    if (extracted.length > 0) {
      setSessionExpiredState(false);
    }
  }

  function handlePasswordChangeText(e: string) {
    setPassword(e);
    setPasswordError('');
    if (e.length > 0) {
      setSessionExpiredState(false);
    }
  }

  async function validatePassword() {
    if (password.length < 4) {
      setPasswordError('Mínimo de 4 caracteres.');
    }
  }

  async function validateCpf() {
    const isValid = cpfValidator.isValid(cpf);
    setCpfIsValid(isValid);
    if (!isValid) {
      return setCpfError('CPF Inválido');
    }
  }

  function resetForm() {
    setCpf('');
    setCpfError('');
    setCpfIsValid(false);
    setPassword('');
    setPasswordError('');
  }

  async function onSubmit() {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      const instalacao = await getInstallAttributionData();

      const res = await api.post('/login_b', {
        cpf,
        password: stringToHex(password),
        preLogin: true,
        clientId,
        instalacao,
      });

      const {token, user} = res.data;
      await signIn({token, user});

      setShowSign(false);
      onSuccess();
      resetForm();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setCpfError('Login/Senha incorretos');
    }
  }

  useEffect(() => {
    if (cpf.length === 11) {
      validateCpf();
    }
  }, [cpf]);

  return (
    <>
      <Form>
        <FormHeader>
          <FormInfos>
            <Title>Bem-vindo de volta</Title>
            <Subtitle>Acesse sua conta para continuar</Subtitle>
          </FormInfos>
        </FormHeader>

        <FormControl>
          <Label>CPF</Label>
          <AuthInput
            errorStyle={!!cpfError}
            error={cpfError}
            placeholder="000.000.000-00"
            keyboardType="number-pad"
            onChangeText={(raw: string, extracted: any) => {
              return handleCpfChangeText(raw, extracted);
            }}
            mask={'[000].[000].[000]-[00]'}
          />
        </FormControl>
        <FormControl>
          <Label>Senha</Label>
          <AuthInput
            errorStyle={!!passwordError}
            error={passwordError}
            placeholder="* * * * * * * *"
            keyboardType="default"
            autoCapitalize="none"
            onChangeText={handlePasswordChangeText}
            passwordInput
            value={password}
            onBlur={validatePassword}
          />
        </FormControl>
        {sessionExpiredState && (
          <Subtitle style={{color: '#e74c3c', marginBottom: 8}}>
            Sua sessão expirou. Faça login novamente.
          </Subtitle>
        )}
        <OutlineButtonContainer>
          <SignupLink onPress={onForgotPassword} activeOpacity={0.7}>
            <SignupLinkText>Esqueci minha senha</SignupLinkText>
          </SignupLink>
        </OutlineButtonContainer>
      </Form>
      <Footer>
        <Button
          title="Continuar"
          onPress={onSubmit}
          enabled={isSigninEnabled && !isLoading}
          loading={isLoading}
        />
        <SignupRow>
          <SignupText>Não tem conta?</SignupText>
          <SignupLink onPress={onSignup} activeOpacity={0.7}>
            <SignupLinkText>Criar conta</SignupLinkText>
          </SignupLink>
        </SignupRow>
      </Footer>
    </>
  );
}
